export class EventQueue {
    items = [];
    head = 0;
    deferred = null;
    observer;
    beforeObserver;
    /** Registry-resolution подключает snapshot eligibility в момент enqueue. */
    setEnqueueObserver(observer) {
        this.observer = observer;
    }
    setBeforeApplyObserver(observer) {
        this.beforeObserver = observer;
    }
    /** Снять узкий pre-reducer snapshot (сейчас — onLeave placement CardMoved). */
    prepare(event) {
        this.beforeObserver?.(event);
    }
    /** Положить событие в хвост очереди. */
    enqueue(event) {
        if (this.deferred !== null) {
            this.deferred.push(event);
            // Damage eligibility/placement фиксируется до simultaneous deaths. Для
            // UnitDied/EffectRemoved и прочих deferred facts snapshot откладывается до
            // flush, когда вся волна уже физически завершена (нет инициативы A/B).
            if (event.type === 'DamageDealt' || event.type === 'AttackResolved')
                this.observer?.(event);
        }
        else {
            this.items.push(event);
            this.observer?.(event);
        }
    }
    /**
     * Временно применять события reducer-ом, но не отдавать их диспетчеру.
     * Нужен фазе основного боя: весь damage + смерти + sweep должны физически
     * завершиться до первого реактивного DamageDealt/UnitDied-хука (S11).
     */
    beginDeferral() {
        if (this.deferred !== null)
            throw new Error('EventQueue.beginDeferral: deferral уже активен');
        if (!this.isEmpty())
            throw new Error('EventQueue.beginDeferral: основная очередь должна быть пуста');
        this.deferred = [];
    }
    /** Перенести удержанные события в FIFO-хвост и снова разрешить dispatch. */
    flushDeferred() {
        if (this.deferred === null)
            throw new Error('EventQueue.flushDeferred: deferral не активен');
        for (const event of this.deferred) {
            if (event.type !== 'DamageDealt' && event.type !== 'AttackResolved')
                this.observer?.(event);
        }
        this.items.push(...this.deferred);
        this.deferred = null;
    }
    get isDeferring() {
        return this.deferred !== null;
    }
    /** Снять событие с головы очереди; пустая очередь — ошибка (драйвер проверяет isEmpty). */
    dequeue() {
        const event = this.items[this.head];
        if (event === undefined) {
            throw new Error('EventQueue.dequeue: очередь пуста (проверяйте isEmpty перед снятием)');
        }
        this.head += 1;
        // Периодически отпускаем уже обработанный префикс, чтобы не держать память.
        if (this.head >= 1024 && this.head * 2 >= this.items.length) {
            this.items = this.items.slice(this.head);
            this.head = 0;
        }
        return event;
    }
    /**
     * Зафиксировать текущий конец очереди для транзиентного lifecycle-barrier.
     * Cursor валиден, пока текущий dispatch-frame не завершён: в этот промежуток
     * очередь только пополняется и не снимает/не компактизирует элементы.
     */
    captureTail() {
        return this.items.length;
    }
    /**
     * Перенести события, добавленные после cursor, перед ранее ожидавшим хвостом.
     * Это узкий примитив resolution-драйвера для depth-first lifecycle-каскада;
     * порядок внутри старого хвоста и внутри добавленного сегмента сохраняется.
     */
    promoteSince(cursor) {
        if (!Number.isInteger(cursor) || cursor < this.head || cursor > this.items.length) {
            throw new Error(`EventQueue.promoteSince: невалидный cursor ${String(cursor)}`);
        }
        if (cursor === this.items.length || cursor === this.head)
            return;
        const appended = this.items.splice(cursor);
        this.items.splice(this.head, 0, ...appended);
    }
    /** Сколько событий ещё не снято. */
    get size() {
        return this.items.length - this.head;
    }
    isEmpty() {
        return this.size === 0;
    }
}
//# sourceMappingURL=queue.js.map